import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import classNames from "classnames";
import { Dimmer } from "semantic-ui-react";
import Table from "../../../components/Table/Table";
import { EditActions, editActions } from "../../../actions/editActions";
import {
  isEditActiveSelector,
  editedObjectIdSelector,
  editedObjectIdentifierSelector
} from "../../../reducers/editReducer";
import _ from "lodash";
import EditDoorEnumeration from "../../Editing/EditDoorEnumeration";
import EditDoorEventType from "../../Editing/EditDoorEventType";
import { mainActions } from "../../../actions/mainActions";
import { Filter } from "../../../components/Table/Migration/LegacyTypes";
import { setEventTypeExtraProperties } from "../../../utils/pdApiUtils";
import { pdApiActions } from "../../../actions/pdApiActions";

type StateProps = {
  pdApi: State.PdApi | undefined;
  columns: State.ColumnMap;
  mainState: State.Main;
  editedObjectId: number | undefined;
  isEditActive: boolean;
  objectIdentifier: string;
  isLoadingEventEnumerations: boolean;
};

type DispatchProps = {
  editActions: typeof editActions;
  mainActions: typeof mainActions;
  pdApiActions: typeof pdApiActions;
};

type OwnProps = {
  subMenuRoute: State.AnySubMenu;
  toggleModal: () => void;
  dataFieldName: keyof State.PdApi | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editor?: any;
  setColumnFilters: (subMenuRoute: State.AnySubMenu, columnFilter: Filter[]) => void;
};

// Props of the component
export type DoorEnumTableProps = StateProps & DispatchProps & OwnProps;

type DoorEnumTableState = {
  isModalOpen: boolean;
};

export class DoorEnumTable extends React.Component<DoorEnumTableProps, DoorEnumTableState> {
  state = {
    isModalOpen: false
  };
  private fetchEventTabValues = () => {
    const { subMenuRoute, pdApi, pdApiActions } = this.props;
    if (!pdApi) return;
      // eslint-disable-next-line
      const EventTabValues: any = pdApi[subMenuRoute.charAt(0).toLowerCase() + subMenuRoute.slice(1)
          .split(" ").join("").replace(".", "") as keyof State.PdApi];
      if (EventTabValues.length === 0) {
        pdApiActions.fetchDoorEvent(subMenuRoute);
        if (subMenuRoute === "Door Event Types") { pdApiActions.fetchDoorEvent("Door Event Classes"); }
      }
  };

  componentDidUpdate(prevProps: DoorEnumTableProps): void { //on click
    if (prevProps.subMenuRoute !== this.props.subMenuRoute) {
      this.props.mainActions.setLastEditedObjectId(undefined);
      this.fetchEventTabValues();      
    }
  }
  
  componentDidMount(): void { //on load
    // make throttle function
    this.handleFilterChangeDebounced = _.debounce(this.handleFilterChangeDebounced, 500);
    if(window.location.href.includes("door-event-classes") && this.props.pdApi["doorEventTypes"].length === 0) {
      this.props.pdApiActions.fetchDoorEvent("Door Event Classes");
    }
    this.fetchEventTabValues();
  }

  private handleFilterChangeDebounced = (changedFilters: Filter[]) => {
    this.props.setColumnFilters(this.props.subMenuRoute, changedFilters);
  };
    componentWillUnmount(): void {
    this.props.mainActions.setLastEditedObjectId(undefined);
  }

  render(): JSX.Element {
    const { columns, pdApi, mainState, isEditActive, dataFieldName, subMenuRoute, isLoadingEventEnumerations } = this.props;
    if (!dataFieldName) {
      return <div>Error : missing dataFieldName</div>;
    }

    let startingPage;
    const { editSize, pageSize, isShortenTextEnabled } = mainState;
    const selectedColumns: State.Column[] = columns[subMenuRoute as keyof State.ColumnMap];
    const editor = dataFieldName === "doorEventTypes" ? EditDoorEventType : EditDoorEnumeration;
    let rows: Api.AnyItem[] = [];

    if(dataFieldName === "doorEventTypes")
    {
      const {doorEventTypes} = pdApi;
      const eventTypeRows: Api.EventTypesEnum[] = doorEventTypes.map(row =>
        setEventTypeExtraProperties(row, pdApi)
      );

      rows = eventTypeRows;
      startingPage = mainState.lastEditedObjectId
        ? (rows.findIndex(eventType => eventType.enumValue === mainState.lastEditedObjectId) / // tslint:disable-line: no-bitwise
            mainState.pageSize) >>
          0
        : 0;
    }
    else {
        let selectedData: Api.EventEnumeration[];
      if (typeof pdApi[dataFieldName] === typeof Array) {
        selectedData = pdApi[dataFieldName] as Api.EventEnumeration[];
      } else {
        selectedData = _.values(pdApi[dataFieldName]);
      }
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rows = selectedData;
      startingPage = mainState.lastEditedObjectId
        ? (rows.findIndex(enumeration => this.isEnumerationSelected(enumeration)) / // tslint:disable-line: no-bitwise
          mainState.pageSize) >>
        0
        : 0;
    }
    
    return (
      <div className="flex-1">
        <div className={"flex-1 IOEventEnumTable"}>
          <Table
            key={this.props.subMenuRoute} // this forces re-render when view changes to reset page to 0
            startingPage={startingPage}
            rows={rows}
            columns={selectedColumns}
            isHidingTextActive={isShortenTextEnabled}
            handleRowSelection={this.props.editActions.select}
            isRowSelected={this.isEnumerationSelected}
            pageSize={pageSize}
            handleFilterChange={this.handleFilterChangeDebounced}
            loading={isLoadingEventEnumerations}
            loadingText={"Fetching " + subMenuRoute + "..."}
          />
        </div>
        {isEditActive && (
          <Dimmer active={true} page={true}>
            <div
              className={classNames("edit-view", {
                "xlg-edit-view": false,
                "xs-edit-view": false
              })}
              style={{ flex: editSize , width : editSize == 0.3 ? "500px" : "1000px" }}
            >
              {React.createElement(editor as any, {
                ...this.props,
                toggleModal: this.props.toggleModal,
                dataFieldName: dataFieldName
              })}
            </div>
          </Dimmer>
        )}
      </div>
    );
  }

  private isEnumerationSelected = (enumeration: Api.AnyItem): boolean => {
    const editedObjectId = this.props.editedObjectId ?? this.props.mainState.lastEditedObjectId;
    // Enumerations can have different fields used as id, we check if any of them matches editedID
    if (editedObjectId !== undefined) {
      if (
        enumeration.enumValue === editedObjectId ||
        enumeration.value === editedObjectId ||
        enumeration.id === editedObjectId
      ) {
        return true;
      }
    }
    return false;
  };
}

const mapStateToProps = (state: State.Root, ownProps: OwnProps) => ({
  columns: state.columns,
  pdApi: state.pdApi,
  mainState: state.main,
  isEditActive: isEditActiveSelector(state.editView),
  editedObjectId: editedObjectIdSelector(state.editView),
  objectIdentifier: editedObjectIdentifierSelector(state.editView, ownProps.subMenuRoute),  
  isLoadingEventEnumerations: state.loadingStatus.isLoadingEventEnumerations,
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch),
  mainActions: bindActionCreators({ ...mainActions }, dispatch),
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(DoorEnumTable);

